import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { CalendarIcon, InfoIcon, CheckIcon, Sparkles } from 'lucide-react';
import { CONSENT_CONFIG, calculateExpiryDate } from '../../constants/uaeStandards';
import { AnimatedScreen, staggerContainer, staggerItem } from '../ui/AnimatedScreen';
import { Tooltip } from '../ui/Tooltip';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
export interface ConsentDurationScreenProps {
  tppName: string;
  onContinue: (durationMonths: number, historicalPeriod?: {
    from: Date;
    to: Date;
  }) => void;
  requiresHistoricalData?: boolean;
}
export function ConsentDurationScreen({
  tppName,
  onContinue,
  requiresHistoricalData = false
}: ConsentDurationScreenProps) {
  const [selectedDuration, setSelectedDuration] = useState(CONSENT_CONFIG.defaultDuration);
  // Set default historical period to "Last 6 months"
  const getDefaultHistoricalDates = () => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - 6);
    return {
      from: from.toISOString().split('T')[0],
      to: to.toISOString().split('T')[0]
    };
  };
  const defaultDates = requiresHistoricalData ? getDefaultHistoricalDates() : {
    from: '',
    to: ''
  };
  const [historicalFrom, setHistoricalFrom] = useState(defaultDates.from);
  const [historicalTo, setHistoricalTo] = useState(defaultDates.to);
  const handleContinue = () => {
    const historicalPeriod = requiresHistoricalData && historicalFrom && historicalTo ? {
      from: new Date(historicalFrom),
      to: new Date(historicalTo)
    } : undefined;
    onContinue(selectedDuration, historicalPeriod);
  };
  const expiryDate = calculateExpiryDate(selectedDuration);
  const recommendedDuration = 6;
  return <AnimatedScreen data-id="element-554">
      <div className="space-y-6" data-id="element-555">
        <div data-id="element-556">
          <h2 className="text-xl font-bold mb-2 leading-tight" style={{
          fontFamily: OPENFINANCE_THEME.fonts.display,
          color: OPENFINANCE_THEME.colors.primary700
        }} data-id="element-557">
            Choose consent duration
          </h2>
          <p className="text-sm leading-relaxed" style={{
          color: OPENFINANCE_THEME.colors.textLight,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-558">
            How long can {tppName} access your data?
          </p>
        </div>

        {/* Duration Options with OpenFinance-OS styling */}
        <motion.div variants={staggerContainer} initial="hidden" animate="show" className="space-y-3" data-id="element-559">
          {CONSENT_CONFIG.durationOptions.map(months => {
          const isSelected = selectedDuration === months;
          const isRecommended = months === recommendedDuration;
          const expiry = calculateExpiryDate(months);
          return <motion.button key={months} variants={staggerItem} onClick={() => setSelectedDuration(months)} whileHover={{
            scale: 1.01
          }} whileTap={{
            scale: 0.99
          }} className="w-full p-4 border-2 transition-all text-left relative" style={{
            borderColor: isSelected ? OPENFINANCE_THEME.colors.primary500 : OPENFINANCE_THEME.colors.border,
            backgroundColor: isSelected ? OPENFINANCE_THEME.colors.primary50 : 'white',
            borderRadius: OPENFINANCE_THEME.radius.lg,
            boxShadow: isSelected ? OPENFINANCE_THEME.shadows.md : 'none'
          }} data-id="element-560">
                <div className="flex items-center justify-between" data-id="element-561">
                  <div className="flex items-center gap-3" data-id="element-562">
                    <motion.div animate={isSelected ? {
                  scale: [1, 1.1, 1]
                } : {}} transition={{
                  duration: 0.3
                }} className="w-10 h-10 flex items-center justify-center" style={{
                  backgroundColor: isSelected ? OPENFINANCE_THEME.colors.primary500 : OPENFINANCE_THEME.colors.primary50,
                  borderRadius: OPENFINANCE_THEME.radius.md
                }} data-id="element-563">
                      <CalendarIcon className="w-5 h-5" style={{
                    color: isSelected ? 'white' : OPENFINANCE_THEME.colors.primary500
                  }} data-id="element-564" />
                    </motion.div>
                    <div data-id="element-565">
                      <div className="flex items-center gap-2" data-id="element-566">
                        <span className="font-semibold" style={{
                      color: OPENFINANCE_THEME.colors.text,
                      fontFamily: OPENFINANCE_THEME.fonts.body
                    }} data-id="element-567">
                          {months} {months === 1 ? 'month' : 'months'}
                        </span>
                        {isRecommended && <Tooltip content="Recommended for personal finance management - provides enough history for insights while maintaining security" data-id="element-568">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{
                        backgroundColor: OPENFINANCE_THEME.colors.accent300,
                        color: OPENFINANCE_THEME.colors.primary700
                      }} data-id="element-569">
                              <Sparkles className="w-3 h-3" data-id="element-570" />
                              Recommended
                            </span>
                          </Tooltip>}
                      </div>
                      <div className="text-xs" style={{
                    color: OPENFINANCE_THEME.colors.textMuted
                  }} data-id="element-571">
                        Expires: {expiry}
                      </div>
                    </div>
                  </div>
                  {isSelected && <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} className="w-6 h-6 rounded-full flex items-center justify-center" style={{
                backgroundColor: OPENFINANCE_THEME.colors.primary500
              }} data-id="element-572">
                      <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-573" />
                    </motion.div>}
                </div>
              </motion.button>;
        })}
        </motion.div>

        {/* Expiry Summary */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="p-4" style={{
        backgroundColor: OPENFINANCE_THEME.colors.primary50,
        border: `2px solid ${OPENFINANCE_THEME.colors.primary200}`,
        borderRadius: OPENFINANCE_THEME.radius.lg
      }} data-id="element-574">
          <div className="flex items-start gap-3" data-id="element-575">
            <InfoIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-576" />
            <div className="text-sm" style={{
            color: OPENFINANCE_THEME.colors.primary700
          }} data-id="element-577">
              <div className="font-semibold mb-1" data-id="element-578">
                Your consent will expire on:
              </div>
              <div className="text-lg font-bold mb-2" style={{
              fontFamily: OPENFINANCE_THEME.fonts.display
            }} data-id="element-579">
                {expiryDate}
              </div>
              <div className="text-xs opacity-80" data-id="element-580">
                You can withdraw this consent at any time before it expires
                through your Consent Management settings.
              </div>
            </div>
          </div>
        </motion.div>

        {/* Historical Data Period */}
        {requiresHistoricalData && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="space-y-3" data-id="element-581">
            <div className="flex items-center gap-2" data-id="element-582">
              <div className="text-sm font-semibold" style={{
            color: OPENFINANCE_THEME.colors.text,
            fontFamily: OPENFINANCE_THEME.fonts.body
          }} data-id="element-583">
                Historical transaction data period
              </div>
              <Tooltip content="Select the date range for past transactions you want to share. This helps provide better financial insights." data-id="element-584">
                <InfoIcon className="w-4 h-4 cursor-help" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-585" />
              </Tooltip>
            </div>
            <p className="text-xs" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-586">
              Select the date range for past transactions you want to share
            </p>

            {/* Quick select buttons */}
            <div className="flex gap-2" data-id="element-587">
              {[{
            label: 'Last 3 months',
            months: 3
          }, {
            label: 'Last 6 months',
            months: 6
          }, {
            label: 'Last year',
            months: 12
          }].map(range => <button key={range.months} onClick={() => {
            const to = new Date();
            const from = new Date();
            from.setMonth(from.getMonth() - range.months);
            setHistoricalFrom(from.toISOString().split('T')[0]);
            setHistoricalTo(to.toISOString().split('T')[0]);
          }} className="px-3 py-1.5 text-xs font-medium transition-colors" style={{
            backgroundColor: OPENFINANCE_THEME.colors.primary50,
            color: OPENFINANCE_THEME.colors.primary600,
            borderRadius: OPENFINANCE_THEME.radius.sm
          }} data-id="element-588">
                  {range.label}
                </button>)}
            </div>

            <div className="grid grid-cols-2 gap-3" data-id="element-589">
              <div data-id="element-590">
                <label className="block text-xs font-medium mb-2" style={{
              color: OPENFINANCE_THEME.colors.textLight
            }} data-id="element-591">
                  From Date
                </label>
                <input type="date" value={historicalFrom} onChange={e => setHistoricalFrom(e.target.value)} max={new Date().toISOString().split('T')[0]} className="w-full px-3 py-2 border-2 focus:outline-none focus:ring-2 focus:border-transparent text-sm" style={{
              borderColor: OPENFINANCE_THEME.colors.border,
              borderRadius: OPENFINANCE_THEME.radius.md,
              '--tw-ring-color': OPENFINANCE_THEME.colors.primary500
            } as React.CSSProperties} data-id="element-592" />
              </div>
              <div data-id="element-593">
                <label className="block text-xs font-medium mb-2" style={{
              color: OPENFINANCE_THEME.colors.textLight
            }} data-id="element-594">
                  To Date
                </label>
                <input type="date" value={historicalTo} onChange={e => setHistoricalTo(e.target.value)} max={new Date().toISOString().split('T')[0]} min={historicalFrom} className="w-full px-3 py-2 border-2 focus:outline-none focus:ring-2 focus:border-transparent text-sm" style={{
              borderColor: OPENFINANCE_THEME.colors.border,
              borderRadius: OPENFINANCE_THEME.radius.md,
              '--tw-ring-color': OPENFINANCE_THEME.colors.primary500
            } as React.CSSProperties} data-id="element-595" />
              </div>
            </div>
          </motion.div>}

        {/* Continue Button with ALTAREQ branding */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} data-id="element-596">
          <button onClick={handleContinue} disabled={requiresHistoricalData && (!historicalFrom || !historicalTo)} className="w-full px-6 py-4 text-white font-semibold transition-all hover:opacity-90 disabled:opacity-50" style={{
          background: 'linear-gradient(90deg, #00BCD4 0%, #1976D2 50%, #000000 100%)',
          borderRadius: '50px'
        }} data-id="element-597">
            <div className="flex items-center justify-center gap-3" data-id="element-598">
              <img src="https://cdn.magicpatterns.com/uploads/8Zyr2eTymP8hQsszbaoPhJ/White_Mark.svg" alt="ALTAREQ" className="h-6 w-6 object-contain" data-id="element-599" />
              <span data-id="element-600">Proceed using Al Tareq</span>
            </div>
          </button>
        </motion.div>

        {/* Withdrawal Rights */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.7
      }} className="text-center" data-id="element-601">
          <div className="inline-flex items-center gap-2 text-xs" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-602">
            <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{
            backgroundColor: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-603">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-604">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-605" />
              </svg>
            </div>
            <span className="font-medium" data-id="element-606">
              You can withdraw consent anytime
            </span>
          </div>
        </motion.div>
      </div>
    </AnimatedScreen>;
}